import { App } from "vue";
import { trace, context } from "@opentelemetry/api";
import type { Tracer } from "@opentelemetry/api";

// Get Tracer
const tracer: Tracer = trace.getTracer("@vue/lifecycle-hooks");

// Watch lists for components:
//   - WatchComponents is a list of component names where we measure the entire lifecycle
//   - watchStartupList List is a list of component names where we measure beforeCreate, onCreated, and onMounted
//   - watchUpdateList List is a list of component names where we measure onBeforeUpdate and onUpdated
//   - watchShutdownList List is a list of component names where we measure onBeforeUnmount and onUnmounted
const watchComponents: string[] = ["RouterView"];
const watchStartupList: Set<string> = new Set([...watchComponents]);
const watchUpdateList: Set<string> = new Set([...watchComponents]);
const watchShutdownList: Set<string> = new Set([...watchComponents]);

export default {
  install(app: App) {
    app.mixin({
      beforeCreate() {
        const componentName: string = this.$options.name || "unknown component";

        // Start a span for the beforeCreate lifecycle hook
        if (watchStartupList.has(componentName)) {
          const parentSpan = tracer.startSpan(
            `[${componentName}] mounting cycle`
          );
          const ctx = trace.setSpan(context.active(), parentSpan);
          const beforeCreateSpan = tracer.startSpan(
            `[${componentName}] onBeforeCreate`,
            undefined,
            ctx
          );
          this.__spans = [parentSpan, beforeCreateSpan];
          this.__ctx = ctx;
        }

        // This has been "removed" in Vue 3 but is now part of the setup() and is still tracked.
        // So we need to run it here
        const originalBeforeCreate = this.beforeCreate;
        if (typeof this.beforeCreate === "function") {
          this.beforeCreate = function () {
            originalBeforeCreate.apply(this);
          };
        }
      },
      created() {
        const componentName = this.$options.name || "unknown component";
        const [parentSpan, beforeCreateSpan] = this.__spans || [];

        // End and Remove previous lifecycle hook span
        if (beforeCreateSpan) {
          beforeCreateSpan.end();
          this.__spans.pop();
        }

        // Create new span if parentSpan exists
        if (parentSpan) {
          const createdSpan = tracer.startSpan(
            `[${componentName}] onCreated`,
            undefined,
            this.__ctx
          );

          this.__spans.push(createdSpan);
        }

        // This has been "removed" in Vue 3 but is now part of the setup() and is still tracked.
        // So we need to run it here
        const originalCreate = this.created;
        if (typeof this.created === "function") {
          this.beforeCreate = function () {
            originalCreate.apply(this);
          };
        }
      },
      mounted() {
        const componentName = this.$options.name || "unknown component";
        const [parentSpan, createdSpan] = this.__spans || [];

        // End and Remove previous lifecycle hook span
        if (createdSpan) {
          createdSpan.end();
          this.__spans.pop();
        }

        // Create new span if parentSpan exists
        if (parentSpan) {
          const mountedSpan = tracer.startSpan(
            `[${componentName}] onMounted`,
            undefined,
            this.__ctx
          );

          // Finds and calls the original lifecycle hook before ending the span
          if (typeof this.onMounted === "function") {
            const originalOnMounted = this.onMounted;
            this.onMounted = function () {
              originalOnMounted.apply(this);
              mountedSpan.end();
            };
          } else {
            mountedSpan.end();
          }
          parentSpan.end();
        }
      },
      beforeUpdate() {
        const componentName = this.$options.name || "unknown component";

        if (watchUpdateList.has(componentName)) {
          const parentSpan = tracer.startSpan(
            `[${componentName}] update cycle`
          );
          const ctx = trace.setSpan(context.active(), parentSpan);
          const beforeUpdateSpan = tracer.startSpan(
            `[${componentName}] onBeforeUpdate`,
            undefined,
            ctx
          );

          this.__spans = [parentSpan, beforeUpdateSpan];
          this.__ctx = ctx;
        }
      },
      updated() {
        const componentName = this.$options.name || "unknown component";
        const [parentSpan, beforeUpdateSpan] = this.__spans || [];

        // End and Remove previous lifecycle hook span
        if (beforeUpdateSpan) {
          beforeUpdateSpan.end();
          this.__spans.pop();
        }

        // Create new span if parentSpan exists
        if (parentSpan) {
          const updatedSpan = tracer.startSpan(
            `[${componentName}] onUpdated`,
            undefined,
            this.__ctx
          );

          // Finds and calls the original lifecycle hook before ending the span
          if (typeof this.onUpdated === "function") {
            const originalOnUpdated = this.onUpdated;
            this.onUpdated = function () {
              originalOnUpdated.apply(this);
              updatedSpan.end();
            };
          } else {
            updatedSpan.end();
          }
          parentSpan.end();
        }
      },
      beforeUnmount() {
        const componentName = this.$options.name || "unknown component";

        if (watchShutdownList.has(componentName)) {
          const parentSpan = tracer.startSpan(
            `[${componentName}] unmount cycle`
          );
          const ctx = trace.setSpan(context.active(), parentSpan);
          const beforeUnmountSpan = tracer.startSpan(
            `[${componentName}] onBeforeUnmount`,
            undefined,
            ctx
          );

          this.__spans = [parentSpan, beforeUnmountSpan];
          this.__ctx = ctx;
        }
      },
      unmounted() {
        const componentName = this.$options.name || "unknown component";
        const [parentSpan, beforeUnmountSpan] = this.__spans || [];

        // End and Remove previous lifecycle hook span
        if (beforeUnmountSpan) {
          beforeUnmountSpan.end();
          this.__spans.pop();
        }

        // Create new span if parentSpan exists
        if (parentSpan) {
          const unmountedSpan = tracer.startSpan(
            `[${componentName}] onUnmounted`,
            undefined,
            this.__ctx
          );

          // Finds and calls the original lifecycle hook before ending the span
          if (typeof this.onUnmounted === "function") {
            const originalOnUpdated = this.onUnmounted;
            this.onUnmounted = function () {
              originalOnUpdated.apply(this);
              unmountedSpan.end();
            };
          } else {
            unmountedSpan.end();
          }
          parentSpan.end();
        }
      },
    });
  },
};
